import { useEffect, useRef, useCallback } from 'react';

/**
 * useWebSocket — connects to the backend WebSocket and calls onMessage on every scan event.
 * Automatically reconnects on disconnect.
 */
export default function useWebSocket(onMessage) {
  const wsRef         = useRef(null);
  const reconnectRef  = useRef(null);
  const unmountedRef  = useRef(false);

  const connect = useCallback(() => {
    if (unmountedRef.current) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    // In production, connect to same host (nginx proxies /ws to backend).
    // In dev (CRA proxy), same logic applies.
    const url = `${protocol}://${window.location.host}/ws`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen    = () => { if (reconnectRef.current) clearTimeout(reconnectRef.current); };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'scan' && onMessage) onMessage(data);
        } catch (_) {}
      };
      ws.onclose = () => {
        if (!unmountedRef.current) {
          reconnectRef.current = setTimeout(connect, 3000);
        }
      };
      ws.onerror = () => ws.close();
    } catch (_) {}
  }, [onMessage]);

  useEffect(() => {
    connect();
    return () => {
      unmountedRef.current = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);
}
